import { useEffect, useRef } from 'react';

// Credit balances change only when the listener does something the app
// already knows about (a conversion, a Story Time season, a Paddle checkout),
// so we refresh on those moments instead of polling every 30 s. Every open
// tab used to cost two /api/credits calls a minute, even in the background.
export const CREDITS_CHANGED_EVENT = 'byopdf:credits-changed';

export function notifyCreditsChanged() {
  window.dispatchEvent(new Event(CREDITS_CHANGED_EVENT));
}

// Refunds (a failed job) and admin grants happen server-side, so a slow
// fallback still runs, but only while the tab is visible.
const FALLBACK_MS = 5 * 60 * 1000;
const MIN_FOCUS_GAP_MS = 60 * 1000;
// Paddle's checkout.completed fires before our webhook has credited the
// account; look again a little later.
const AFTER_CHECKOUT_MS = [4000, 15000];

export function useCreditsRefresh(refresh, enabled = true) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!enabled) return undefined;
    let last = Date.now();
    const timers = [];
    const run = () => {
      last = Date.now();
      refreshRef.current();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - last > MIN_FOCUS_GAP_MS) run();
    };
    const onCheckout = (event) => {
      if (event.detail && event.detail.name === 'checkout.completed') {
        AFTER_CHECKOUT_MS.forEach((ms) => timers.push(setTimeout(run, ms)));
      }
    };
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') run();
    }, FALLBACK_MS);

    window.addEventListener(CREDITS_CHANGED_EVENT, run);
    window.addEventListener('paddle:checkout-event', onCheckout);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      timers.forEach(clearTimeout);
      window.removeEventListener(CREDITS_CHANGED_EVENT, run);
      window.removeEventListener('paddle:checkout-event', onCheckout);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);
}
